import React, { useEffect, useState, useRef } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, CircularProgress, Button, Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import getNewsFromFirebase,{ deleteNewsById, NewsItem } from '../../services/newsService';
import { DocumentSnapshot } from 'firebase/firestore';
import Loader from '../../Component/Loader';
import { NewsFormData } from '../../Forms/newsFormUtils';

interface AdminNewsTableProps {
  onEdit?: (news: NewsFormData & { id: string }) => void;
}

const AdminNewsTable = ({ onEdit }: AdminNewsTableProps) => {
  const [newsList, setNewsList] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastVisible, setLastVisible] = useState<DocumentSnapshot | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [rowLoading, setRowLoading] = useState<string | null>(null);

  const isFetchingRef = useRef(false);

  useEffect(() => {
    fetchInitialNews();
  }, []);

  const fetchInitialNews = async () => {
    try {
      setLoading(true);
      const { news, lastVisible } = await getNewsFromFirebase(6);
      setNewsList(news);
      setLastVisible(lastVisible);
      if (news.length < 6) setHasMore(false);
    } catch (err) {
      toast.error("Failed to load news.");
    } finally {
      setLoading(false);
    }
  };

  const fetchNextNews = async () => {
    if (isFetchingRef.current || !hasMore) return;
    isFetchingRef.current = true;
    try {
      const { news: nextNews, lastVisible: newLastVisible } = await getNewsFromFirebase(6, lastVisible);
      setLastVisible(newLastVisible);
      if (nextNews.length === 0) {
        setHasMore(false);
      } else {
        setNewsList((prev) => [...prev, ...nextNews]);
      }
    } catch (err) {
      toast.error("Failed to load more news.");
    } finally {
      isFetchingRef.current = false;
    }
  };

  const handleDelete = async (id: string) => {
    setRowLoading(id);
    try {
      await deleteNewsById(id);
      toast.success("News deleted");
      setNewsList(prev => prev.filter(news => news.id !== id));
    } catch (err) {
      toast.error("Failed to delete news");
    } finally {
      setRowLoading(null);
    }
  };

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Thumbnail</TableCell>
            <TableCell>Title</TableCell>
            <TableCell>Heading</TableCell>
            <TableCell>Short Description</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={5}>
                <Loader />
              </TableCell>
            </TableRow>
          ) : newsList.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography align="center">No news available</Typography>
              </TableCell>
            </TableRow>
          ) : (
            newsList.map((news) => (
              <TableRow key={news.id}>
                <TableCell>
                  <img src={news.thumbnailImage} alt={news.title} style={{ width: 80, height: 60, objectFit: 'cover' }} />
                </TableCell>
                <TableCell>{news.title}</TableCell>
                <TableCell>{news.heading}</TableCell>
                <TableCell>{news.shortdescription}</TableCell>
                <TableCell>
                  <IconButton
                    onClick={() =>
                      onEdit &&
                      onEdit({
                        ...news,
                        id: news.id,
                        thumbnailImage: news.thumbnailImage,
                        images: news.images || [],
                        shortDescription: news.shortdescription,
                        description: news.description
                      })
                    }
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(news.id)} disabled={rowLoading === news.id}>
                    {rowLoading === news.id ? <CircularProgress size={20} /> : <DeleteIcon />}
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {hasMore && (
        <div className="text-center my-4">
          <Button variant="outlined" onClick={fetchNextNews}>
            Load More
          </Button>
        </div>
      )}
    </TableContainer>
  );
};

export default AdminNewsTable;
